import { PublicOrganizationOrganizationTypeChoices } from "@graphql/generated/graphql.g";
import {
  OrganizationFragment,
  useOrganizationsQuery,
} from "@graphql/organizations/organizations.g";
import {
  createColumnHelper,
  type ColumnDef,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Table } from "@design-system/Table/Table";
import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";

import { DeleteOrganizationModal } from "@components/Modals";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  OrganizationsTableConfig,
  renderCount,
  renderCreatedAt,
  renderName,
  renderOrganizationType,
  renderStytchOrganizationId,
  renderUpdatedAt,
} from "./OrganizationsTableConfig";
import { OrganizationTableColumns } from "./UI/OrganizationsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type Props = {
  initialPageIndex?: number;
  nameSearch?: string;
  stytchOrganizationIdSearch?: string;
  organizationTypeFilter?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
};

export const OrganizationsTable = ({
  initialPageIndex = 0,
  nameSearch,
  stytchOrganizationIdSearch,
  organizationTypeFilter,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = PAGE_SIZE,
}: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });

  const [selectedOrganizationToDelete, setSelectedOrganizationToDelete] = useState<OrganizationFragment | null>(null);

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedNameSearch = useDebounce(nameSearch, SEARCH_DEBOUNCE_TIME);
  const debouncedStytchOrganizationIdSearch = useDebounce(
    stytchOrganizationIdSearch,
    SEARCH_DEBOUNCE_TIME,
  );
  const debouncedOrganizationTypeFilter = useDebounce(
    organizationTypeFilter,
    SEARCH_DEBOUNCE_TIME,
  );

  const organizationsQuery = useOrganizationsQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    name_Icontains: debouncedNameSearch || undefined,
    stytchOrganizationId: debouncedStytchOrganizationIdSearch || undefined,
    organizationType:
      debouncedOrganizationTypeFilter === "MSP"
        ? PublicOrganizationOrganizationTypeChoices.Msp
        : debouncedOrganizationTypeFilter === "DISTRIBUTOR"
          ? PublicOrganizationOrganizationTypeChoices.Distributor
          : undefined,
    orderBy: ConfigurableTableSystem.generateSortParameter(
      OrganizationsTableConfig.columns,
      sorting,
    ),
  });

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );
  const isOrganizationsLoading = organizationsQuery.isLoading;
  const isOrganizationsFetching = organizationsQuery.isFetching;

  const totalCount =
    organizationsQuery.data?.organizations?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [
    debouncedNameSearch,
    debouncedStytchOrganizationIdSearch,
    debouncedOrganizationTypeFilter,
    pageSize,
  ]);

  const data = useMemo(() => {
    return (
      organizationsQuery.data?.organizations?.edges
        .map((edge) => edge?.node)
        .filter((organization) => !!organization) ?? []
    );
  }, [organizationsQuery.data]);

  const configuredColumns = useMemo(() => {
    const configWithRenderers = {
      ...OrganizationsTableConfig,
      columns: Object.fromEntries(
        Object.entries(OrganizationsTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (value: unknown, row: OrganizationFragment) => {
                const columnKey = key as OrganizationTableColumns;

                switch (columnKey) {
                  case OrganizationTableColumns.name:
                    return renderName(value, row);
                  case OrganizationTableColumns.organizationType:
                    return renderOrganizationType(value);
                  case OrganizationTableColumns.stytchOrganizationId:
                    return renderStytchOrganizationId(value);
                  case OrganizationTableColumns.clientCount:
                    return renderCount(value);
                  case OrganizationTableColumns.userCount:
                    return renderCount(value);
                  case OrganizationTableColumns.employeeCount:
                    return renderCount(value);
                  case OrganizationTableColumns.createdAt:
                    return renderCreatedAt(value);
                  case OrganizationTableColumns.updatedAt:
                    return renderUpdatedAt(value);
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof OrganizationsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as OrganizationTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings]);

  const allColumns = useMemo(() => {
    const columnHelper = createColumnHelper<OrganizationFragment>();
    const columns: ColumnDef<OrganizationFragment, unknown>[] = [
      ...configuredColumns,
    ];

    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: { sticky: true, actions: true },
        cell: (props) => (
          <Box gap={2}>
            <Dropdown trigger={<IconButton size="sm" icon="faEllipsisVertical" />}>
              <Dropdown.Option
                label="Delete tenant"
                icon="faTrash"
                onSelect={() => setSelectedOrganizationToDelete(props.row.original)}
              />
            </Dropdown>
          </Box>
        ),
      }),
    );

    return columns;
  }, [configuredColumns]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  const handleOrganizationDeleted = useCallback(() => {
    organizationsQuery.refetch();
  }, [organizationsQuery]);

  return (
    <>
      <Table<OrganizationFragment, string>
        columns={allColumns as ColumnDef<OrganizationFragment, string>[]}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isOrganizationsLoading}
        isFetching={isOrganizationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={setSorting}
        showFooter={showFooter}
        showHeader={showHeader}
        showPageSizeDropdown={showPageSizeDropdown}
        columnVisibility={columnSettings}
      />
      {selectedOrganizationToDelete && (
        <DeleteOrganizationModal
          isOpen={true}
          onClose={() => setSelectedOrganizationToDelete(null)}
          organizationId={selectedOrganizationToDelete.id}
          organizationName={selectedOrganizationToDelete.name}
          onSuccess={handleOrganizationDeleted}
        />
      )}
    </>
  );
};
